import { useState } from 'react';
import { Check, Copy, KeyRound, X } from 'lucide-react';

interface Props {
  sourceId: string;
  secret: string;
  onClose: () => void;
}

const API_BASE = import.meta.env.VITE_API_BASE_URL || window.location.origin;

/**
 * Mostrado uma única vez após criar a origem ou rotacionar o segredo — a API
 * nunca devolve o segredo em texto claro de novo.
 */
// Nomes exatos das Repository variables que o passo de deploy do pipeline lê —
// exibidos com botão de copiar para eliminar erro de digitação no Bitbucket.
const VAR_URL = 'UPTIME_WEBHOOK_URL';
const VAR_SECRET = 'UPTIME_DEPLOY_WEBHOOK_SECRET';

export function WebhookSecretModal({ sourceId, secret, onClose }: Props) {
  const [copied, setCopied] = useState<string | null>(null);

  // Endpoint do AUTO-RELATO do pipeline (curl no bitbucket-pipelines.yml do repo
  // monitorado) — o gatilho adotado, que garante a branch. /api/webhooks/bitbucket/
  // é o caminho do webhook nativo do Bitbucket (payload sem branch confiável) e NÃO
  // é o que vai nas Repository variables.
  const url = `${API_BASE}/api/webhooks/pipeline/${sourceId}`;

  async function copy(value: string, which: string) {
    await navigator.clipboard.writeText(value);
    setCopied(which);
    setTimeout(() => setCopied(null), 2000);
  }

  function VariableName({ name, which }: { name: string; which: string }) {
    return (
      <div className="mt-1.5 flex items-center gap-1.5 text-xs text-gray-500 dark:text-gray-400">
        <span>Variável no Bitbucket:</span>
        <code className="rounded bg-gray-100 px-1.5 py-0.5 font-mono dark:bg-gray-800">{name}</code>
        <button
          onClick={() => copy(name, which)}
          title="Copiar nome da variável"
          className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
        >
          {copied === which ? <Check size={13} /> : <Copy size={13} />}
        </button>
      </div>
    );
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm">
      <div className="w-full max-w-xl rounded-lg border border-gray-200 bg-white shadow-xl dark:border-gray-800 dark:bg-gray-900">
        <div className="flex items-center justify-between border-b border-gray-200 px-6 py-4 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <span className="rounded-md bg-amber-100 p-1.5 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300">
              <KeyRound size={18} />
            </span>
            <h2 className="text-lg font-semibold">Configure o webhook no Bitbucket</h2>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
          >
            <X size={20} />
          </button>
        </div>

        <div className="space-y-4 px-6 py-5">
          <p className="text-sm text-gray-600 dark:text-gray-400">
            No repositório, vá em <strong>Repository settings → Webhooks → Add webhook</strong> e
            marque o evento <strong>Build status updated</strong>.
          </p>
          <p className="text-sm text-gray-600 dark:text-gray-400">
            Se o repositório notifica pelo <strong>passo de deploy do pipeline</strong>, crie as
            variáveis abaixo em <strong>Repository settings → Repository variables</strong> (ou no
            Deployment environment), com exatamente estes nomes — o secret marcado como{' '}
            <strong>Secured</strong>.
          </p>

          <div>
            <label className="mb-1 block text-sm font-medium">URL</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={url}
                className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-xs dark:border-gray-600 dark:bg-gray-800"
              />
              <button
                onClick={() => copy(url, 'url')}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {copied === 'url' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <VariableName name={VAR_URL} which="url-var" />
          </div>

          <div>
            <label className="mb-1 block text-sm font-medium">Secret</label>
            <div className="flex gap-2">
              <input
                readOnly
                value={secret}
                className="w-full rounded-md border border-gray-300 bg-gray-50 px-3 py-2 font-mono text-xs dark:border-gray-600 dark:bg-gray-800"
              />
              <button
                onClick={() => copy(secret, 'secret')}
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-800"
              >
                {copied === 'secret' ? <Check size={16} /> : <Copy size={16} />}
              </button>
            </div>
            <VariableName name={VAR_SECRET} which="secret-var" />
            <p className="mt-1.5 text-xs text-amber-600 dark:text-amber-400">
              Copie agora — este segredo não será exibido novamente. Se perdê-lo, gere um novo pela
              ação “Novo segredo”.
            </p>
          </div>
        </div>

        <div className="flex justify-end border-t border-gray-200 px-6 py-4 dark:border-gray-700">
          <button
            onClick={onClose}
            className="rounded-md bg-blue-600 px-4 py-2 text-sm font-medium text-white hover:bg-blue-700"
          >
            Já copiei
          </button>
        </div>
      </div>
    </div>
  );
}
